// src/pages/Consultation/modals/PriceAdjustmentModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  FiX,
  FiPercent,
  FiDollarSign,
  FiTag,
  FiAlertCircle,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import { addPriceAdjustmentThunk } from "../../../../store/thunks/consultationThunks";

const ADJUSTMENT_TYPES = [
  {
    value: "ITEM_DISCOUNT",
    label: "Item Discount",
    description: "Discount on specific item",
    icon: FiTag,
  },
  {
    value: "CART_DISCOUNT",
    label: "Cart Discount",
    description: "Overall cart discount",
    icon: FiPercent,
  },
  {
    value: "MAKING_CHARGES_WAIVER",
    label: "Making Charges Waiver",
    description: "Reduce/waive making charges",
    icon: FiDollarSign,
  },
  {
    value: "EXCHANGE_BONUS",
    label: "Exchange Bonus",
    description: "Extra value for exchange",
    icon: FiTag,
  },
  {
    value: "LOYALTY_DISCOUNT",
    label: "Loyalty Discount",
    description: "VIP/loyalty customer discount",
    icon: FiTag,
  },
  {
    value: "PRICE_MATCH",
    label: "Price Match",
    description: "Match competitor price",
    icon: FiDollarSign,
  },
  {
    value: "CUSTOM",
    label: "Custom Adjustment",
    description: "Other custom adjustment",
    icon: FiTag,
  },
];

const ADJUSTMENT_METHODS = [
  {
    value: "PERCENTAGE",
    label: "Percentage Off",
    symbol: "%",
    placeholder: "e.g., 10 for 10%",
  },
  {
    value: "FIXED_AMOUNT",
    label: "Fixed Amount Off",
    symbol: "₹",
    placeholder: "e.g., 5000",
  },
  {
    value: "OVERRIDE_PRICE",
    label: "Override Price",
    symbol: "₹",
    placeholder: "e.g., 45000",
  },
];

const PriceAdjustmentModal = ({
  isOpen,
  onClose,
  session,
  adminInfo,
  cartItems = [],
  exchangeItems = [],
}) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    adjustmentType: "",
    scope: "CART", // ITEM | CART | EXCHANGE_ITEM
    targetProductId: "",
    targetExchangeItemId: "",
    adjustmentMethod: "PERCENTAGE",
    value: "",
    overridePrice: "",
    reason: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);

  // Update scope based on adjustment type
  useEffect(() => {
    if (formData.adjustmentType === "EXCHANGE_BONUS") {
      setFormData((prev) => ({ ...prev, scope: "EXCHANGE_ITEM" }));
    } else if (formData.adjustmentType === "ITEM_DISCOUNT") {
      setFormData((prev) => ({ ...prev, scope: "ITEM" }));
    } else if (
      ["CART_DISCOUNT", "LOYALTY_DISCOUNT", "MAKING_CHARGES_WAIVER"].includes(
        formData.adjustmentType
      )
    ) {
      setFormData((prev) => ({ ...prev, scope: "CART" }));
    }
  }, [formData.adjustmentType]);

  // Check if requires approval (business logic)
  useEffect(() => {
    const checkApproval = () => {
      // ✅ MATCH BACKEND LOGIC:

      // Percentage > 10% (NOT 15%)
      if (formData.adjustmentMethod === "PERCENTAGE" && formData.value > 10) {
        return true;
      }

      // Fixed amount > ₹5,000 (NOT ₹10,000)
      if (
        formData.adjustmentMethod === "FIXED_AMOUNT" &&
        formData.value > 5000
      ) {
        return true;
      }

      // Override price ALWAYS requires approval
      if (formData.adjustmentMethod === "OVERRIDE_PRICE") {
        return true;
      }

      // Exchange bonus > ₹5,000
      if (
        formData.adjustmentType === "EXCHANGE_BONUS" &&
        formData.value > 5000
      ) {
        return true;
      }

      return false;
    };

    setRequiresApproval(checkApproval());
  }, [formData.adjustmentMethod, formData.adjustmentType, formData.value]);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.adjustmentType) {
      newErrors.adjustmentType = "Please select adjustment type";
    }

    if (formData.scope === "ITEM" && !formData.targetProductId) {
      newErrors.targetProductId = "Please select a product";
    }

    if (formData.scope === "EXCHANGE_ITEM" && !formData.targetExchangeItemId) {
      newErrors.targetExchangeItemId = "Please select an exchange item";
    }

    if (!formData.adjustmentMethod) {
      newErrors.adjustmentMethod = "Please select adjustment method";
    }

    if (formData.adjustmentMethod === "OVERRIDE_PRICE") {
      if (!formData.overridePrice || formData.overridePrice <= 0) {
        newErrors.overridePrice = "Please enter a valid override price";
      }
    } else {
      if (!formData.value || formData.value <= 0) {
        newErrors.value = "Please enter a valid value";
      }

      if (formData.adjustmentMethod === "PERCENTAGE" && formData.value > 100) {
        newErrors.value = "Percentage cannot exceed 100%";
      }
    }

    if (!formData.reason || formData.reason.trim().length < 10) {
      newErrors.reason = "Please provide a reason (min 10 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);

    try {
      // In handleSubmit method
      const adjustmentData = {
        sessionId: session._id,
        adjustmentData: {
          adjustmentType: formData.adjustmentType,
          scope: formData.scope,
          targetProductId: formData.targetProductId || undefined,
          targetExchangeItemId: formData.targetExchangeItemId || undefined,
          adjustment: {
            type: formData.adjustmentMethod,
            value:
              formData.adjustmentMethod === "OVERRIDE_PRICE"
                ? undefined
                : parseFloat(formData.value),
            overridePrice:
              formData.adjustmentMethod === "OVERRIDE_PRICE"
                ? parseFloat(formData.overridePrice)
                : undefined,
          },
          reason: formData.reason,
          notes: formData.notes || undefined,
        },
        adminCode: adminInfo.adminCode,
        adminName: adminInfo.adminName,
      };

      const result = await dispatch(
        addPriceAdjustmentThunk(adjustmentData)
      ).unwrap();

      if (result.requiresApproval) {
        toast.success("Adjustment created - Manager approval required");
      } else {
        toast.success("Price adjustment applied successfully");
      }

      handleClose();
    } catch (error) {
      console.error("Add adjustment error:", error);
      toast.error(error || "Failed to add adjustment");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!submitting) {
      setFormData({
        adjustmentType: "",
        scope: "CART",
        targetProductId: "",
        targetExchangeItemId: "",
        adjustmentMethod: "PERCENTAGE",
        value: "",
        overridePrice: "",
        reason: "",
        notes: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  console.log(">>>>", cartItems);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiPercent className="text-xl text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Add Price Adjustment
                </h3>
                <p className="text-sm text-gray-500">
                  Session: {session?.sessionCode}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <FiX className="text-xl text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Adjustment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Adjustment Type *
              </label>
              <div className="grid grid-cols-2 gap-3">
                {ADJUSTMENT_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          adjustmentType: type.value,
                        }))
                      }
                      disabled={submitting}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        formData.adjustmentType === type.value
                          ? "border-primary bg-primary-light bg-opacity-10"
                          : "border-gray-300 hover:border-primary"
                      }`}
                    >
                      <Icon className="text-lg mb-1 text-primary" />
                      <p className="font-medium text-sm text-gray-900">
                        {type.label}
                      </p>
                      <p className="text-xs text-gray-500">
                        {type.description}
                      </p>
                    </button>
                  );
                })}
              </div>
              {errors.adjustmentType && (
                <p className="mt-2 text-sm text-red-500">
                  {errors.adjustmentType}
                </p>
              )}
            </div>

            {/* Scope-specific Selection */}
            {formData.scope === "ITEM" && cartItems.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Product *
                </label>
                <select
                  name="targetProductId"
                  value={formData.targetProductId}
                  onChange={handleChange}
                  disabled={submitting}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.targetProductId
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select a product</option>
                  {cartItems.map((item) => (
                    <option key={item.product._id} value={item.product._id}>
                      {item.product.name} -{" "}
                      {item.product.productId?.toLocaleString()}
                    </option>
                  ))}
                </select>
                {errors.targetProductId && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.targetProductId}
                  </p>
                )}
              </div>
            )}

            {formData.scope === "EXCHANGE_ITEM" && exchangeItems.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Exchange Item *
                </label>
                <select
                  name="targetExchangeItemId"
                  value={formData.targetExchangeItemId}
                  onChange={handleChange}
                  disabled={submitting}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.targetExchangeItemId
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select an exchange item</option>
                  {exchangeItems.map((item) => (
                    <option
                      key={item.exchangeItemId}
                      value={item.exchangeItemId}
                    >
                      {item.description} - ₹{item.agreedValue?.toLocaleString()}
                    </option>
                  ))}
                </select>
                {errors.targetExchangeItemId && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.targetExchangeItemId}
                  </p>
                )}
              </div>
            )}

            {/* Adjustment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Adjustment Method *
              </label>
              <div className="grid grid-cols-3 gap-3">
                {ADJUSTMENT_METHODS.map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        adjustmentMethod: method.value,
                      }))
                    }
                    disabled={submitting}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      formData.adjustmentMethod === method.value
                        ? "border-primary bg-primary-light bg-opacity-10"
                        : "border-gray-300 hover:border-primary"
                    }`}
                  >
                    <p className="text-xl font-bold text-primary mb-1">
                      {method.symbol}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {method.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Value Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.adjustmentMethod === "OVERRIDE_PRICE"
                  ? "New Price *"
                  : "Adjustment Value *"}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  {ADJUSTMENT_METHODS.find(
                    (m) => m.value === formData.adjustmentMethod
                  )?.symbol || ""}
                </span>
                <input
                  type="number"
                  name={
                    formData.adjustmentMethod === "OVERRIDE_PRICE"
                      ? "overridePrice"
                      : "value"
                  }
                  value={
                    formData.adjustmentMethod === "OVERRIDE_PRICE"
                      ? formData.overridePrice
                      : formData.value
                  }
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder={
                    ADJUSTMENT_METHODS.find(
                      (m) => m.value === formData.adjustmentMethod
                    )?.placeholder
                  }
                  step={
                    formData.adjustmentMethod === "PERCENTAGE" ? "0.01" : "1"
                  }
                  min="0"
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.value || errors.overridePrice
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
              </div>
              {(errors.value || errors.overridePrice) && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.value || errors.overridePrice}
                </p>
              )}
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Adjustment *
              </label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                disabled={submitting}
                rows={3}
                placeholder="Explain why this adjustment is needed (min 10 characters)..."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
                  errors.reason ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.reason && (
                <p className="mt-1 text-sm text-red-500">{errors.reason}</p>
              )}
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={submitting}
                rows={2}
                placeholder="Any additional context..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Approval Warning */}
            {requiresApproval && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
                <FiAlertCircle className="text-orange-600 text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-900">
                    Manager Approval Required
                  </p>
                  <p className="text-sm text-orange-700 mt-1">
                    This adjustment requires manager approval before it can be
                    applied. The pricing will not change until approved.
                  </p>
                </div>
              </div>
            )}
          </form>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white flex items-center justify-end gap-3 px-6 py-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
            >
              {submitting
                ? "Adding..."
                : requiresApproval
                ? "Submit for Approval"
                : "Apply Adjustment"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceAdjustmentModal;
